import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';

import { BasicButton } from '@components/Button';
import {
  CollectionCard,
  ManagementLayout,
  PageTitle,
  PaginatedList,
  SearchInput,
} from '@components/shared';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { toErrorMessage } from '@util/toErrorMessage';

const CATEGORY_OPTIONS = [
  { value: '1', label: 'Consistency' },
  { value: '2', label: 'Volume' },
  { value: '3', label: 'Milestone' },
  { value: '4', label: 'Strength' },
];

const CRITERIA_OPTIONS = [
  { value: '1', label: 'Session count' },
  { value: '2', label: 'Streak days' },
  { value: '3', label: 'Session volume (lb)' },
  { value: '4', label: 'Lifetime volume (lb)' },
  { value: '5', label: 'Exercise max count' },
  { value: '6', label: 'Block complete' },
];

function AchievementCreateForm({ onCreated, onCancel }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('1');
  const [criteriaType, setCriteriaType] = useState('1');
  const [criteriaValue, setCriteriaValue] = useState(1);
  const [allowsMultiple, setAllowsMultiple] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [saving, setSaving] = useState(false);

  function onSubmit(event) {
    event.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    axios
      .post('/achievements/', {
        name: name.trim(),
        description: description.trim() || null,
        category: Number(category),
        criteria_type: Number(criteriaType),
        criteria_value: Number(criteriaValue) || 0,
        allows_multiple: allowsMultiple,
        sort_order: Number(sortOrder) || 0,
      })
      .then(({ data }) => {
        const achievement = data.response?.[0];
        toast.success('Achievement created');
        onCreated(achievement);
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSaving(false));
  }

  return (
    <Paper p="md" withBorder radius="md" component="form" onSubmit={onSubmit}>
      <Title order={4} mb="sm">
        New achievement
      </Title>
      <Stack gap="sm">
        <TextInput
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <Textarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          minRows={2}
          autosize
        />
        <Select
          label="Category"
          data={CATEGORY_OPTIONS}
          value={category}
          onChange={(value) => setCategory(value || '1')}
        />
        <Select
          label="Award when"
          data={CRITERIA_OPTIONS}
          value={criteriaType}
          onChange={(value) => setCriteriaType(value || '1')}
        />
        <NumberInput
          label="Criteria value"
          value={criteriaValue}
          onChange={(value) => setCriteriaValue(value ?? 0)}
          min={0}
        />
        <NumberInput
          label="Sort order"
          value={sortOrder}
          onChange={(value) => setSortOrder(value ?? 0)}
        />
        <Checkbox
          label="Allow multiple awards"
          checked={allowsMultiple}
          onChange={(e) => setAllowsMultiple(e.currentTarget.checked)}
        />
        <Group>
          <BasicButton
            text="Create achievement"
            type="submit"
            loading={saving}
            disabled={saving || !name.trim()}
            color="var(--brand-brick)"
            variant="filled"
            style={{
              background: 'var(--brand-brick)',
              borderColor: 'var(--brand-brick)',
            }}
            primary
          />
          <BasicButton
            text="Cancel"
            type="button"
            onClick={onCancel}
            disabled={saving}
            color="gray"
            variant="outline"
          />
        </Group>
      </Stack>
    </Paper>
  );
}

export default function AdminAchievements() {
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [listKey, setListKey] = useState(0);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  function onCreated(achievement) {
    setCreating(false);
    setListKey((key) => key + 1);
    queryClient.invalidateQueries({ queryKey: ['paginated-list'] });
    if (achievement?.id) {
      navigate(`/achievements/${achievement.id}`);
    }
  }

  return (
    <div>
      <PageTitle
        title="Achievements"
        subtitle="Definitions athletes can earn into their Trophy Case"
        icon={entityIcon.achievement()}
        iconColor={entityColor.achievement}
        actions={
          !creating ? (
            <BasicButton
              text="Add achievement"
              onClick={() => setCreating(true)}
              color={entityColor.achievement}
              variant="light"
              leftSection={entityIcon.achievement()}
            />
          ) : null
        }
      />
      <ManagementLayout
        createComponent={
          creating ? (
            <AchievementCreateForm
              onCreated={onCreated}
              onCancel={() => setCreating(false)}
            />
          ) : null
        }
        dataComponent={
          <div>
            <SearchInput
              value={name}
              onChange={setName}
              placeholder="Search by name..."
            />
            <PaginatedList
              key={listKey}
              requestInfo={{
                url: '/retrieve-achievements/',
                data: { name },
              }}
              displayFormatter={(item) => {
                const achievement = item.original;
                return (
                  <CollectionCard
                    key={achievement.id}
                    to={`/achievements/${achievement.id}`}
                    title={achievement.name}
                    meta={
                      <Text size="sm" c="dimmed">
                        {achievement.category_formatted} ·{' '}
                        {achievement.criteria_type_formatted} ≥{' '}
                        {achievement.criteria_value}
                      </Text>
                    }
                    badge={achievement.status_formatted}
                    badgeColor={
                      achievement.status_formatted === 'active'
                        ? 'teal'
                        : achievement.status_formatted === 'retired'
                          ? 'gray'
                          : 'red'
                    }
                  />
                );
              }}
            />
          </div>
        }
      />
    </div>
  );
}
